import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { createUniver, LocaleType, mergeLocales } from '@univerjs/presets';
import { UniverSheetsCorePreset } from '@univerjs/preset-sheets-core';
import { UniverSheetsConditionalFormattingPreset } from '@univerjs/preset-sheets-conditional-formatting';
import { UniverSheetsDataValidationPreset } from '@univerjs/preset-sheets-data-validation';
import { UniverSheetsFilterPreset } from '@univerjs/preset-sheets-filter';
import { UniverSheetsSortPreset } from '@univerjs/preset-sheets-sort';
import { ILayoutService } from '@univerjs/ui';
import CoreEnUS from '@univerjs/preset-sheets-core/locales/en-US';
import CFEnUS from '@univerjs/preset-sheets-conditional-formatting/locales/en-US';
import DVEnUS from '@univerjs/preset-sheets-data-validation/locales/en-US';
import FilterEnUS from '@univerjs/preset-sheets-filter/locales/en-US';
import SortEnUS from '@univerjs/preset-sheets-sort/locales/en-US';
import '@univerjs/preset-sheets-core/lib/index.css';
import '@univerjs/preset-sheets-conditional-formatting/lib/index.css';
import '@univerjs/preset-sheets-data-validation/lib/index.css';
import '@univerjs/preset-sheets-filter/lib/index.css';
import '@univerjs/preset-sheets-sort/lib/index.css';
import { buildWorkbookData, parseA1 } from './workbookData';

const ExcelWorkbook = forwardRef(({ sheets, tasks, snapshot, onChange }, ref) => {
  const hostRef = useRef(null);
  const apiRef = useRef(null);
  const univerRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const container = document.createElement('div');
    container.style.height = '100%';
    host.append(container);
    const { univer, univerAPI } = createUniver({
      locale: LocaleType.EN_US,
      locales: { [LocaleType.EN_US]: mergeLocales(CoreEnUS, CFEnUS, DVEnUS, FilterEnUS, SortEnUS) },
      presets: [
        UniverSheetsCorePreset({
          container,
          footer: { menus: false },
          menu: { 'sheet.menu.paste': { hidden: true }, 'sheet.menu.paste-special': { hidden: true }, 'sheet.command.paste': { hidden: true } },
        }),
        UniverSheetsConditionalFormattingPreset(),
        UniverSheetsDataValidationPreset(),
        UniverSheetsFilterPreset(),
        UniverSheetsSortPreset(),
      ],
    });
    univerAPI.createWorkbook(snapshot || buildWorkbookData(sheets, tasks));
    apiRef.current = univerAPI;
    univerRef.current = univer;
    window.__hfUniver = univerAPI;
    let sub;
    if (onChange) {
      sub = univerAPI.addEvent(univerAPI.Event.CommandExecuted, (e) => {
        if (e?.id && e.id.includes('mutation')) onChange();
      });
    }
    return () => {
      sub?.dispose?.();
      apiRef.current = null;
      univerRef.current = null;
      delete window.__hfUniver;
      queueMicrotask(() => { univer.dispose(); container.remove(); });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    goTo(sheetName, a1) {
      const wb = apiRef.current?.getActiveWorkbook();
      const sheet = wb?.getSheetByName(sheetName);
      if (!sheet) return;
      wb.setActiveSheet(sheet);
      const first = a1.split(':')[0];
      setTimeout(() => {
        sheet.getRange(first).activate();
        // Move keyboard focus into the grid so the candidate can type straight away
        try { univerRef.current?.__getInjector().get(ILayoutService).focus(); } catch { /* grid not ready */ }
      }, 50);
    },
    collect() {
      const wb = apiRef.current?.getActiveWorkbook();
      if (!wb) return null;
      const cells = {};
      tasks.forEach((t) => {
        const sheet = wb.getSheetByName(t.sheet);
        if (!sheet || !t.read_range) return;
        const range = sheet.getRange(t.read_range);
        cells[t.id] = { values: range.getValues(), formulas: range.getFormulas() };
      });
      const rules = {};
      wb.getSheets().forEach((s) => {
        const name = s.getSheetName();
        rules[name] = {
          cf: (s.getConditionalFormattingRules?.() || []).map((r) => ({ ranges: r.ranges, rule: r.rule })),
          dv: (s.getDataValidations?.() || []).map((d) => ({
            type: d.rule?.type, operator: d.rule?.operator, formula1: d.rule?.formula1,
            formula2: d.rule?.formula2, ranges: d.rule?.ranges,
          })),
        };
      });
      return { cells, rules, snapshot: wb.save() };
    },
  }));

  return <div ref={hostRef} className="h-full w-full" data-testid="univer-spreadsheet-container" />;
});

ExcelWorkbook.displayName = 'ExcelWorkbook';
export { parseA1 };
export default ExcelWorkbook;
